'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { Palette, Loader2, AlertCircle, Eye, Lock, Info, ChevronRight, Upload, Image, X } from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { getCdnUrl } from '@/lib/utils/cdnUrl';

interface AppearanceSettingsProps {
  agentId: string;
  agentName: string;
  initialSettings: {
    hidePoweredBy?: boolean;
    wideLogo?: string;
    squareLogo?: string;
    wideLogoDark?: string;
    squareLogoDark?: string;
  };
}

export default function AppearanceSettings({ agentId, agentName, initialSettings }: AppearanceSettingsProps) {
  const { t } = useTranslation();
  const [hidePoweredBy, setHidePoweredBy] = useState(initialSettings.hidePoweredBy || false);
  const [wideLogo, setWideLogo] = useState(initialSettings.wideLogo || '');
  const [squareLogo, setSquareLogo] = useState(initialSettings.squareLogo || '');
  const [wideLogoDark, setWideLogoDark] = useState(initialSettings.wideLogoDark || '');
  const [squareLogoDark, setSquareLogoDark] = useState(initialSettings.squareLogoDark || '');
  const [uploadingWideLogo, setUploadingWideLogo] = useState(false);
  const [uploadingSquareLogo, setUploadingSquareLogo] = useState(false);
  const [uploadingWideLogoDark, setUploadingWideLogoDark] = useState(false);
  const [uploadingSquareLogoDark, setUploadingSquareLogoDark] = useState(false);
  const [isSavingHidePowered, setIsSavingHidePowered] = useState(false);
  const [error, setError] = useState('');
  const wideLogoInputRef = useRef<HTMLInputElement>(null);
  const squareLogoInputRef = useRef<HTMLInputElement>(null);
  const wideLogoDarkInputRef = useRef<HTMLInputElement>(null);
  const squareLogoDarkInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Use feature flags hook
  const { chatHideVectorizeEnabled, chatThemingEnabled, isLoading: loadingFlags } = useFeatureFlags(agentId);

  // Use agent hook for mutation and to get organizationId
  const { updateAgent, agent, configDoc } = useAgent(agentId);
  const organizationId = configDoc?.organizationId;

  // Auto-save function
  const autoSave = useCallback(
    async (settingsToSave: any) => {
      try {
        await updateAgent.mutateAsync(settingsToSave);
        showToast(t('admin.appearance.autoSaved'), 'success');

        // Invalidate cache to refresh agent data
        await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      } catch (err) {
        showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
      }
    },
    [updateAgent, showToast, t, queryClient, agentId]
  );

  const handleLogoUpload = async (file: File, type: 'wide' | 'square' | 'wideDark' | 'squareDark') => {
    if (!file) return;

    const setUploading =
      type === 'wide'
        ? setUploadingWideLogo
        : type === 'square'
        ? setUploadingSquareLogo
        : type === 'wideDark'
        ? setUploadingWideLogoDark
        : setUploadingSquareLogoDark;
    const setLogo = type === 'wide' ? setWideLogo : type === 'square' ? setSquareLogo : type === 'wideDark' ? setWideLogoDark : setSquareLogoDark;

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('logo', file);
    formData.append('type', type);

    try {
      const response = await fetch(`/api/chat/agents/${agentId}/branding`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('admin.appearance.branding.uploadError'));
      }

      const data = await response.json();
      setLogo(data.logoUrl);

      // Auto-save the logo setting
      const logoField = type === 'wide' ? 'wideLogo' : type === 'square' ? 'squareLogo' : type === 'wideDark' ? 'wideLogoDark' : 'squareLogoDark';

      await updateAgent.mutateAsync({ [logoField]: data.logoUrl });

      // Show success toast
      showToast(t('admin.appearance.branding.uploadSuccess'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.appearance.branding.uploadError'));
      showToast(err instanceof Error ? err.message : t('admin.appearance.branding.uploadError'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoRemove = async (type: 'wide' | 'square' | 'wideDark' | 'squareDark') => {
    const setLogo = type === 'wide' ? setWideLogo : type === 'square' ? setSquareLogo : type === 'wideDark' ? setWideLogoDark : setSquareLogoDark;
    setLogo('');

    // Auto-save the removed logo
    const logoField = type === 'wide' ? 'wideLogo' : type === 'square' ? 'squareLogo' : type === 'wideDark' ? 'wideLogoDark' : 'squareLogoDark';

    try {
      await updateAgent.mutateAsync({ [logoField]: '' });

      // Show success toast
      showToast(t('admin.appearance.branding.removeSuccess'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
    }
  };

  // Show skeleton loader while loading feature flags
  if (loadingFlags) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Settings sections skeleton */}
          <div className="space-y-8">
            {/* Footer Settings skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="space-y-4">
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse ml-auto"></div>
              </div>
            </div>

            {/* Branding skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-6"></div>
              <div className="space-y-6">
                <div>
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                  <div className="h-3 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-3"></div>
                  <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                  <div className="h-3 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-3"></div>
                  <div className="h-10 w-44 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Theme Settings skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 opacity-50">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
            </div>
          </div>

          {/* Save Button skeleton */}
          <div className="mt-8 flex justify-end">
            <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.appearance.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('admin.appearance.description')}</p>
        </div>

        {/* Settings sections */}
        <div className="space-y-8">
          {/* Footer Settings */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatHideVectorizeEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Eye className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.appearance.footer.title')}</h2>
              {!chatHideVectorizeEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>

            {!chatHideVectorizeEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Eye className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.whiteLabel.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.whiteLabel.description')}</p>
                    <a
                      href={`${
                        process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'
                      }/organization/${organizationId}/admin/billing/changeplan`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                    >
                      {t('upgradeCTA.upgradeToPro')}
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.appearance.footer.hidePoweredByLabel')}</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.appearance.footer.hidePoweredByHelp')}</p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (chatHideVectorizeEnabled && !isSavingHidePowered) {
                      const newValue = !hidePoweredBy;
                      setHidePoweredBy(newValue);
                      setIsSavingHidePowered(true);

                      try {
                        await updateAgent.mutateAsync({ hidePoweredBy: newValue });
                        showToast(t('admin.appearance.autoSaved'), 'success');
                        // Invalidate cache to refresh agent data
                        await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
                      } catch (err) {
                        // Revert on error
                        setHidePoweredBy(!newValue);
                        showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
                      } finally {
                        setIsSavingHidePowered(false);
                      }
                    }
                  }}
                  disabled={!chatHideVectorizeEnabled || isSavingHidePowered}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${hidePoweredBy ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                    ${!chatHideVectorizeEnabled || isSavingHidePowered ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${hidePoweredBy ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>
          </section>

          {/* Branding Section */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatThemingEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Image className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.appearance.branding.title')}</h2>
              {!chatThemingEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>

            {!chatThemingEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Image className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.branding.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.branding.description')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('upgradeCTA.branding.features')}</p>
                    <a
                      href={`${
                        process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'
                      }/organization/${organizationId}/admin/billing/changeplan`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                    >
                      {t('upgradeCTA.upgradeToPro')}
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('admin.appearance.branding.description')}</p>

            {/* Logo Grid */}
            <div className="space-y-6">
              {/* Column Headers */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.appearance.branding.lightMode.label')}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.appearance.branding.lightMode.description')}</p>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.appearance.branding.darkMode.label')}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.appearance.branding.darkMode.description')}</p>
                </div>
              </div>

              {/* Wide Logos Row */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.appearance.branding.wideLogo.label')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('admin.appearance.branding.wideLogo.help')}</p>
                <div className="grid grid-cols-2 gap-4">
                  {/* Light Mode Wide Logo */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="min-h-[100px] flex items-center justify-center">
                      {wideLogo ? (
                        <div className="relative group w-full">
                          <img src={getCdnUrl(wideLogo)} alt="Wide Logo Light" className="h-12 max-w-full mx-auto object-contain" />
                          {chatThemingEnabled && (
                            <button
                              onClick={() => handleLogoRemove('wide')}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => chatThemingEnabled && wideLogoInputRef.current?.click()}
                          disabled={!chatThemingEnabled || uploadingWideLogo}
                          className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-gray-600 hover:text-gray-700 ${
                            !chatThemingEnabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingWideLogo ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {wideLogo && chatThemingEnabled && (
                      <button
                        onClick={() => wideLogoInputRef.current?.click()}
                        disabled={uploadingWideLogo}
                        className="mt-3 w-full px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {/* Dark Mode Wide Logo */}
                  <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                    <div className="min-h-[100px] flex items-center justify-center">
                      {wideLogoDark ? (
                        <div className="relative group w-full">
                          <img src={getCdnUrl(wideLogoDark)} alt="Wide Logo Dark" className="h-12 max-w-full mx-auto object-contain" />
                          {chatThemingEnabled && (
                            <button
                              onClick={() => handleLogoRemove('wideDark')}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => chatThemingEnabled && wideLogoDarkInputRef.current?.click()}
                          disabled={!chatThemingEnabled || uploadingWideLogoDark}
                          className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-600 rounded-lg hover:border-gray-500 transition-colors text-gray-300 ${
                            !chatThemingEnabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingWideLogoDark ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {wideLogoDark && chatThemingEnabled && (
                      <button
                        onClick={() => wideLogoDarkInputRef.current?.click()}
                        disabled={uploadingWideLogoDark}
                        className="mt-3 w-full px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                      >
                        Change
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Square Logos Row */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.appearance.branding.squareLogo.label')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('admin.appearance.branding.squareLogo.help')}</p>
                <div className="grid grid-cols-2 gap-4">
                  {/* Light Mode Square Logo */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="h-[120px] w-[120px] mx-auto flex items-center justify-center">
                      {squareLogo ? (
                        <div className="relative group">
                          <img src={getCdnUrl(squareLogo)} alt="Square Logo Light" className="h-16 w-16 object-contain" />
                          {chatThemingEnabled && (
                            <button
                              onClick={() => handleLogoRemove('square')}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => chatThemingEnabled && squareLogoInputRef.current?.click()}
                          disabled={!chatThemingEnabled || uploadingSquareLogo}
                          className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-gray-600 hover:text-gray-700 ${
                            !chatThemingEnabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingSquareLogo ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {squareLogo && chatThemingEnabled && (
                      <button
                        onClick={() => squareLogoInputRef.current?.click()}
                        disabled={uploadingSquareLogo}
                        className="mt-3 w-full px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {/* Dark Mode Square Logo */}
                  <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                    <div className="h-[120px] w-[120px] mx-auto flex items-center justify-center">
                      {squareLogoDark ? (
                        <div className="relative group">
                          <img src={getCdnUrl(squareLogoDark)} alt="Square Logo Dark" className="h-16 w-16 object-contain" />
                          {chatThemingEnabled && (
                            <button
                              onClick={() => handleLogoRemove('squareDark')}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => chatThemingEnabled && squareLogoDarkInputRef.current?.click()}
                          disabled={!chatThemingEnabled || uploadingSquareLogoDark}
                          className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-600 rounded-lg hover:border-gray-500 transition-colors text-gray-300 ${
                            !chatThemingEnabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingSquareLogoDark ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {squareLogoDark && chatThemingEnabled && (
                      <button
                        onClick={() => squareLogoDarkInputRef.current?.click()}
                        disabled={uploadingSquareLogoDark}
                        className="mt-3 w-full px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                      >
                        Change
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Hidden file inputs */}
              <input
                ref={wideLogoInputRef}
                type="file"
                accept="image/png,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleLogoUpload(file, 'wide');
                  }
                }}
                className="hidden"
              />
              <input
                ref={squareLogoInputRef}
                type="file"
                accept="image/png,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleLogoUpload(file, 'square');
                  }
                }}
                className="hidden"
              />
              <input
                ref={wideLogoDarkInputRef}
                type="file"
                accept="image/png,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleLogoUpload(file, 'wideDark');
                  }
                }}
                className="hidden"
              />
              <input
                ref={squareLogoDarkInputRef}
                type="file"
                accept="image/png,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleLogoUpload(file, 'squareDark');
                  }
                }}
                className="hidden"
              />

              <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.appearance.branding.maxSize')}</p>
            </div>
          </section>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mt-6 flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
